import apiClient from "./apiClient";
import { formatApiError } from "../lib/laravelError";
import { STORAGE_KEYS, storage, mockDelay, ensureSeedData } from "./storage";

ensureSeedData();

function pickPlansList(payload) {
  if (!payload || typeof payload !== "object") return [];
  const raw = payload.plans ?? payload.data;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.plans)) return raw.plans;
  return [];
}

function parseFeatures(description) {
  if (!description || typeof description !== "string") return [];
  return description
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

function mapIntervalLabel(interval, intervalCount = 1) {
  if (!interval) return "mes";
  if (interval === "month") return intervalCount > 1 ? `${intervalCount} meses` : "mes";
  if (interval === "year") return intervalCount > 1 ? `${intervalCount} años` : "año";
  return interval;
}

/**
 * Normaliza un BillingProduct de GET /billing/plans al modelo de UI.
 */
export function mapBillingProductToPlan(product) {
  const planCode = product?.plan?.code ?? product?.key ?? String(product?.id ?? "");
  const unitAmount = Number(product?.unit_amount ?? 0);
  const price = Number.isFinite(unitAmount) ? unitAmount / 100 : 0;
  const description = product?.description?.trim() || "";
  const features = parseFeatures(description);

  return {
    id: planCode,
    key: product?.key ?? planCode,
    name: product?.name || product?.plan?.name || planCode,
    description,
    price,
    currency: String(product?.currency || "EUR").toUpperCase(),
    period: mapIntervalLabel(product?.interval, product?.interval_count),
    features:
      features.length > 0
        ? features
        : description
          ? [description]
          : ["Detalles del plan disponibles al suscribirte"],
    popular: false,
    status: product?.is_active === false ? "inactive" : "active",
    type: product?.type ?? null,
    planId: product?.plan?.id ?? null,
    billingProductId: product?.id ?? null,
  };
}

function markPopularPlan(plans) {
  const subscriptions = plans.filter((p) => p.type === "subscription" || p.type == null);
  const pool = subscriptions.length > 0 ? subscriptions : plans;
  if (pool.length < 2) return plans;

  const paid = [...pool].filter((p) => p.price > 0).sort((a, b) => a.price - b.price);
  const popularId = paid[Math.floor((paid.length - 1) / 2)]?.id;
  if (!popularId) return plans;

  return plans.map((plan) => ({
    ...plan,
    popular: plan.id === popularId,
  }));
}

export const subscriptionService = {
  async listPlans() {
    try {
      const { data } = await apiClient.get("/billing/plans");
      const items = pickPlansList(data)
        .filter((item) => item?.is_active !== false)
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map(mapBillingProductToPlan);

      return markPopularPlan(items);
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudieron cargar los planes"));
    }
  },

  async getCurrent() {
    try {
      const { data } = await apiClient.get("/billing/status");
      const stripeStatus = data?.subscription?.stripe_status;
      const status =
        stripeStatus === "active" || stripeStatus === "trialing"
          ? "active"
          : data?.subscribed
            ? stripeStatus || "active"
            : "inactive";

      return {
        currentPlanId: data?.subscribed ? null : "curioso",
        status,
        renewsAt: data?.subscription?.ends_at ?? data?.subscription?.trial_ends_at ?? null,
        subscribed: Boolean(data?.subscribed),
        raw: data,
      };
    } catch {
      await mockDelay(80);
      return storage.get(STORAGE_KEYS.subscription, {
        currentPlanId: "curioso",
        status: "active",
        renewsAt: null,
      });
    }
  },

  async subscribe(planId) {
    await mockDelay();
    const renewsAt =
      planId && planId !== "curioso" && planId !== "free"
        ? new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString()
        : null;
    const value = {
      currentPlanId: planId,
      status: "active",
      renewsAt,
    };
    storage.set(STORAGE_KEYS.subscription, value);
    return value;
  },
};
